import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Typography, Card, CardContent, Button, Box, Paper, Chip } from '@mui/material';
import { School, VpnKey, ArrowForward } from '@mui/icons-material';
import { useGuest } from '../context/GuestContext';
import { trackGuestEvent } from '../services/guestAnalyticsService';

const cardSx = {
  width: '100%',
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  '&:hover': { boxShadow: 3 },
} as const;

/**
 * Top-level Try as Guest: catalog courses flow; BYOK card disabled (UG-16).
 */
const GuestEntryPage: React.FC = () => {
  const navigate = useNavigate();
  const { ensureSession } = useGuest();

  useEffect(() => {
    ensureSession()
      .then((gid) => trackGuestEvent(gid, { eventType: 'hub_viewed' }))
      .catch(() => {});
  }, [ensureSession]);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={0} sx={{ p: 4 }}>
        <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
          Try as Guest
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Explore courses on the platform without an account. Sign up anytime to save progress.
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 3,
            alignItems: 'stretch',
          }}
        >
          <Box sx={{ flex: { md: '1 1 0' }, minWidth: 0, display: 'flex' }}>
            <Card
              sx={{
                ...cardSx,
                border: 1,
                borderColor: 'primary.main',
                cursor: 'pointer',
              }}
              onClick={() => navigate('/guest/available')}
            >
              <CardContent
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  py: 3,
                  px: 2,
                  height: '100%',
                  boxSizing: 'border-box',
                }}
              >
                <Box sx={{ mb: 2, height: 56, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <School sx={{ fontSize: 56, color: 'primary.main' }} />
                </Box>
                <Box
                  sx={{
                    width: '100%',
                    minHeight: { xs: 'auto', md: 72 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 1.5,
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                    Try available courses
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2, flex: 1, width: '100%', maxWidth: 360, mx: 'auto' }}
                >
                  Search topics, browse by school, or pick a course type. Same guest limits as before (e.g. two topics).
                </Typography>
                <Button
                  variant="contained"
                  endIcon={<ArrowForward />}
                  sx={{ width: '100%', maxWidth: 280, mt: 'auto' }}
                >
                  Browse courses
                </Button>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ flex: { md: '1 1 0' }, minWidth: 0, display: 'flex' }}>
            <Card
              sx={{
                ...cardSx,
                border: 1,
                borderColor: 'grey.300',
                opacity: 0.65,
                cursor: 'not-allowed',
              }}
            >
              <CardContent
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  py: 3,
                  px: 2,
                  height: '100%',
                  boxSizing: 'border-box',
                }}
              >
                <Box sx={{ mb: 1 }}>
                  <Chip label="Coming soon" size="small" color="default" />
                </Box>
                <Box sx={{ mb: 2, height: 56, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <VpnKey sx={{ fontSize: 56, color: 'text.disabled' }} />
                </Box>
                <Box
                  sx={{
                    width: '100%',
                    minHeight: { xs: 'auto', md: 72 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 1.5,
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.3, color: 'text.secondary' }}>
                    Try with your own AI key
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2, flex: 1, width: '100%', maxWidth: 360, mx: 'auto' }}
                >
                  Coming soon — use Browse courses to try Secondary curriculum content.
                </Typography>
                <Button
                  variant="outlined"
                  disabled
                  endIcon={<ArrowForward />}
                  sx={{ width: '100%', maxWidth: 280, mt: 'auto' }}
                >
                  Continue with your key
                </Button>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default GuestEntryPage;
